// csv.ts — one serializer for every "Export CSV" in the app.
//
// The audience is a spreadsheet, usually Excel, usually the bookkeeper's. Three
// things follow from that and live here so no page re-decides them:
//
// - UTF-8 with a BOM and CRLF line ends (RFC 4180). Without the BOM Excel reads
//   a UTF-8 file as Latin-1 and every dash, accent and degree sign lands as
//   mojibake; with it, Sheets and Numbers are unaffected.
// - Quoting only when needed: a cell holding a comma, a quote, a line break, or
//   an outer space is wrapped and its quotes doubled. Everything else stays
//   bare, so a number is still a number when the sheet opens.
// - A formula guard on strings. A cell that opens with = + - @ (or a tab or CR)
//   is executed by Excel and Sheets when the file opens; a vendor name or a
//   free-text note is exactly where such a string could arrive. The guard
//   prefixes a single quote, which the sheet shows as text. Numbers are never
//   guarded: a negative amount is a number, not a formula.

export type CsvValue = string | number | boolean | null | undefined;

const NEEDS_QUOTES = /[",\r\n]|^\s|\s$/;
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** One cell, serialized. Exported for tests; pages call `toCsv`. */
export function csvCell(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  let s = value;
  if (FORMULA_LEAD.test(s)) s = `'${s}`;
  return NEEDS_QUOTES.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Headers first, then one line per row. Ends with a line break. */
export function toCsv(headers: string[], rows: CsvValue[][]): string {
  const lines = [headers, ...rows].map((r) => r.map(csvCell).join(','));
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/** `purchase-orders-2026-08-27.csv`: the page's name and the local date, so a
 *  folder of exports sorts itself. */
export function csvFilename(basename: string, now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${basename}-${y}-${m}-${d}.csv`;
}

/** Hands the file to the browser. The object URL is revoked on the next tick,
 *  after the click has been dispatched. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
